<template>
  <div class="weight-picker-wrapper">
    <!-- Отображение текущего значения веса -->
    <div class="selected-value pb-3" style="font-size: 24pt">
      {{ selectedWeight.toFixed(1) }} кг
    </div>

    <!-- Обёртка с градиентами -->
    <div class="weight-picker-container">
      <!-- Левый градиент -->
      <div class="gradient gradient-left"></div>

      <!-- Прокручиваемый контейнер -->
      <div class="weight-picker" ref="pickerContainer" @scroll="onScroll">
        <div
          class="weight-picker-content"
          :style="{
            width: contentWidth + 'px',
            paddingLeft: viewportPadding + 'px',
            paddingRight: viewportPadding + 'px',
          }"
        >
          <div
            v-for="(value, index) in range"
            :key="index"
            :style="{
              color: isInteger(value) ? '#000' : '#a8a8a8',
            }"
            class="weight-item-wrapper"
          >
            <div
              class="weight-item"
              :style="{
                width: '30px',
                textAlign: 'center',
                lineHeight: '25px',
                fontSize: isInteger(value) ? 14 + 'pt' : 9 + 'pt',
              }"
            >
              {{
                parseFloat(value.toFixed(1) % 1) === 0
                  ? parseFloat(value.toFixed(0))
                  : parseFloat(
                      value.toFixed(1) - Math.trunc(value.toFixed(1))
                    ).toFixed(1) * 10
              }}
            </div>
            <!-- Маленький серый тик под значением -->
            <div
              class="tick"
              :style="{
                height: isInteger(value) ? 15 + 'px' : '7px',
                marginTop: isInteger(value) ? 7 + 'px' : '15px',
                backgroundColor: '#ccc',
              }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Правый градиент -->
      <div class="gradient gradient-right"></div>
    </div>

    <!-- Фиксированная синяя полоска -->
    <div class="indicator"></div>
  </div>
  <div class="d-grid px-2 pb-2 pb-md-5">
    <Button
      label="Сохранить"
      severity="success"
      rounded
      raised
      @click="saveWeight"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";

export default defineComponent({
  name: "WeightPicker",
  props: {
    minWeight: {
      type: Number,
      default: 40, // Минимальный вес
    },
    maxWeight: {
      type: Number,
      default: 200, // Максимальный вес
    },
    step: {
      type: Number,
      default: 0.1, // Шаг (с точностью до 0.1)
    },
    itemWidth: {
      type: Number,
      default: 30, // Ширина одного элемента
    },
    viewportWidth: {
      type: Number,
      default: 250, // Ширина вьюпорта
    },
    initialWeight: {
      type: Number,
      default: 70, // Текущее значение веса при загрузке
    },
  },
  emits: ["update:modelValue", "save"],
  setup(props, { emit }) {
    const pickerContainer = ref<HTMLDivElement | null>(null);
    const scrollPosition = ref(0);

    // Проверка, является ли значение целым
    const isInteger = (value: number) =>
      Math.abs(value - Math.round(value)) < 0.001;
    // Генерация диапазона значений
    const range = computed(() => {
      const values = [];
      for (let i = props.minWeight; i <= props.maxWeight; i += props.step) {
        values.push(parseFloat(i.toFixed(1))); // Округление для избежания ошибок с плавающей точкой
      }
      return values;
    });

    // Общая ширина контента
    const contentWidth = computed(() => range.value.length * props.itemWidth);

    // Отступы для центрирования текущего значения
    const viewportPadding = computed(
      () => props.viewportWidth / 2 - props.itemWidth / 2
    );

    // Стили для элементов
    const itemStyle = computed(() => ({
      /*   width: `${props.itemWidth}px`, */
      width: "30px",
      textAlign: "center",
      lineHeight: "25px",
      fontSize: "15px",
    }));

    // Определение текущего выбранного значения
    const selectedWeight = computed(() => {
      // Центр текущего видимого окна
      const centerPosition =
        scrollPosition.value + props.viewportWidth / 2 - props.itemWidth / 2;
      // Индекс текущего значения
      const index = Math.round(centerPosition / props.itemWidth) - 3;
      return range.value[index] || props.minWeight;
    });

    const onScroll = () => {
      if (pickerContainer.value) {
        scrollPosition.value = pickerContainer.value.scrollLeft;
        emit("update:modelValue", selectedWeight.value);
      }
    };

    // Установка начальной позиции при загрузке
    const setInitialScrollPosition = () => {
      if (pickerContainer.value) {
        const initialIndex = range.value.indexOf(
          parseFloat(props.initialWeight.toFixed(1)) + 0.3
        );
        if (initialIndex !== -1) {
          const scrollLeft =
            initialIndex * props.itemWidth -
            props.viewportWidth / 2 +
            props.itemWidth / 2;
          pickerContainer.value.scrollLeft = scrollLeft;
        }
      }
    };

    onMounted(() => {
      setInitialScrollPosition();
    });

    // Сброс начального веса, если проп `initialWeight` изменяется
    watch(
      () => props.initialWeight,
      () => {
        setInitialScrollPosition();
      }
    );

    const saveWeight = () => {
      emit("save", selectedWeight.value);
    };

    return {
      pickerContainer,
      scrollPosition,
      range,
      contentWidth,
      itemStyle,
      viewportPadding,
      selectedWeight,
      onScroll,
      isInteger,
      saveWeight,
    };
  },
});
</script>

<style scoped>
.weight-picker-wrapper {
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 200px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 8px;
}

.selected-value {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
}

/* Контейнер для градиентов и прокручиваемого содержимого */
.weight-picker-container {
  position: relative;
  width: 100%;
  height: 50px;
  overflow: hidden; /* Градиенты остаются поверх */
}

.weight-picker {
  position: relative;
  width: 100%;
  height: 100%;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch; /* Плавность на iOS устройствах */
  scroll-snap-type: x mandatory;
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 8px;
}

.weight-picker::-webkit-scrollbar {
  display: none; /* Убираем горизонтальную полосу прокрутки */
}

.weight-picker-content {
  display: flex;
  flex-direction: row;
  scroll-snap-align: start;
}

.weight-item-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  height: 100%;
  justify-content: flex-end; /* Выравниваем элементы вниз */
}

.weight-item {
  scroll-snap-align: center;
  user-select: none;
}

.tick {
  width: 2px;
  margin-top: 5px;
  position: relative;
}

/* Фиксированные градиенты */
.gradient {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 30px;
  pointer-events: none; /* Градиенты не блокируют взаимодействие */
  z-index: 5;
}

.gradient-left {
  left: 0;
  background: linear-gradient(to right, rgba(248, 249, 250, 1), transparent);
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  border-left: 1px solid #ccc;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
}

.gradient-right {
  right: 0;
  background: linear-gradient(to left, rgba(248, 249, 250, 1), transparent);
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  border-right: 1px solid #ccc;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
}

/* Фиксированная синяя полоска */
.indicator {
  position: absolute;
  top: 3.8em;
  bottom: 4.5em;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  background-color: #4caf50;
  z-index: 10;
  pointer-events: none;
}
.itemstyle {
  width: "30px";
  text-align: "center";
  line-height: "25px";
  font-size: "15px";
}
</style>
